import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toApiError } from "../../lib/apiError";
import { parseImportDiagnostics } from "../../lib/importDiagnostics";
import { useApiClient } from "../useApiClient";
import type { components } from "../schema";
import { ImportDiagnosticsError } from "./useValidateImport";

type RebindResponse = components["schemas"]["RebindBrokerAccountMaskResponse"];

/**
 * The binding moved between the user seeing it and confirming the re-link.
 *
 * Distinct from a generic failure because the only useful response is to
 * re-read the current state: another tab (or another device) changed which
 * broker account this one accepts, and overwriting a decision the user never
 * saw is what the confirmation exists to prevent.
 */
export class AccountMaskConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AccountMaskConflictError";
  }
}

export type RebindAccountMaskVariables = {
  accountId: string;
  /** The binding the user was shown. The server rejects a stale one. */
  expectedCurrentMask: string;
  /**
   * The statement proving the account's new broker account.
   *
   * The new mask is NOT sent -- the server reads it from this file's header, so
   * re-linking requires possession of a real statement rather than a
   * well-formed string. The file is read header-only and discarded: nothing is
   * imported, and the user re-submits the import afterwards.
   */
  file: File;
};

export function useRebindAccountMask() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<RebindResponse, Error, RebindAccountMaskVariables>({
    mutationFn: async ({ accountId, expectedCurrentMask, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("expected_current_mask", expectedCurrentMask);
      const { data, error, response } = await client.PUT(
        "/api/accounts/{account_id}/broker-account-mask",
        {
          params: { path: { account_id: accountId } },
          body: {
            file,
            expected_current_mask: expectedCurrentMask,
            broker: "tos",
          } as unknown as {
            file: string;
            expected_current_mask: string;
            broker: string;
          },
          bodySerializer: () => formData,
        },
      );
      if (error !== undefined) {
        if (response.status === 413) {
          throw new Error("File is too large. Try a smaller CSV.");
        }
        const message = toApiError(error, "Failed to re-link this account.");
        if (response.status === 409) {
          throw new AccountMaskConflictError(message);
        }
        // An unreadable statement comes back in the same diagnostic shape the
        // import rejections use, so the page renders it with the same catalogue.
        const diagnostics = parseImportDiagnostics(error);
        if (diagnostics.length > 0) {
          throw new ImportDiagnosticsError(message, diagnostics);
        }
        throw new Error(message);
      }
      // The accounts list carries the binding, so it is stale after this.
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      return data;
    },
  });
}
